/**
 * Calculates adaptive y-axis tick values for charts based on actual data values.
 * Uses adaptive rounding intervals based on the magnitude of the data to ensure
 * bars/lines are proportionally visible regardless of data magnitude.
 *
 * @param values - Array of all data values from the chart
 * @param defaultTicks - Fallback ticks when values are empty or all zero
 * @param numberOfIntervals - Number of intervals to divide the y-axis into (default: 5)
 * @param decimals - Decimal places kept on each tick (default: 0). Small-valued series such as cost per km
 *                   need this, otherwise rounding to whole numbers collapses the ticks into duplicates.
 * @returns Array of tick values sorted in descending order
 */
export function calculateChartYTicks(
  values: number[],
  defaultTicks: number[],
  numberOfIntervals: number = 5,
  decimals: number = 0
): number[] {
  if (!values.length) return defaultTicks;

  const overallMax = Math.max(...values);
  if (overallMax === 0) return defaultTicks;

  let roundingInterval: number;
  if (decimals > 0 && overallMax <= 1) {
    roundingInterval = 0.1;
  } else if (overallMax <= 5) {
    roundingInterval = 1;
  } else if (overallMax <= 20) {
    roundingInterval = 5;
  } else if (overallMax <= 100) {
    roundingInterval = 10;
  } else if (overallMax <= 1000) {
    roundingInterval = 100;
  } else if (overallMax <= 10000) {
    roundingInterval = 1000;
  } else if (overallMax <= 100000) {
    roundingInterval = 10000;
  } else {
    roundingInterval = 100000;
  }

  const roundedMax = Math.max(
    Math.ceil(overallMax / roundingInterval) * roundingInterval,
    Math.ceil((overallMax * 1.1) / roundingInterval) * roundingInterval
  );

  const tickInterval = roundedMax / numberOfIntervals;
  const precision = 10 ** decimals;
  const ticks: number[] = [];
  for (let i = 0; i <= numberOfIntervals; i++) {
    ticks.push(Math.round(tickInterval * i * precision) / precision);
  }

  return ticks.sort((a, b) => b - a);
}
